import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  DialogProps,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "@emotion/styled";
import { Form } from "@shared/components/Form";
import { TextField } from "@shared/components/TextField";
import {
  restoreSubscriptionFormSchema,
  RestoreSubscriptionFormSchema,
  TRIAL_DAYS_MAX_LENGTH,
} from "./restore-subscription-helpers";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitButton } from "@avenue-8/sidekick-ui";

const CustomDialogTitle = styled(DialogTitle)`
  display: grid;
  grid-template-columns: auto 40px;
  align-items: center;
  padding-right: 1rem;
`;

interface Props extends DialogProps {
  productName: string;
  userInfo: {
    name: string;
    email: string;
    subscriptionPlan?: {
      name: string;
    };
  };
  open: boolean;
  isLoading: boolean;
  onClose: () => void;
  onRestoreSubscription: (data: { trialDays: number }) => void;
}

export const RestoreSubscriptionModal = (props: Props) => {
  const {
    open,
    userInfo,
    productName,
    isLoading,
    onClose,
    onRestoreSubscription,
    ...rest
  } = props;

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<RestoreSubscriptionFormSchema>({
    resolver: yupResolver(restoreSubscriptionFormSchema),
    reValidateMode: "onChange",
  });

  return (
    <Dialog {...rest} fullWidth maxWidth="sm" onClose={onClose} open={open}>
      <CustomDialogTitle id="restore-subscription-dialog-title">
        <span>Resume Subscription</span>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </CustomDialogTitle>
      <DialogContent>
        <Typography>
          You are about to resume this <strong>{productName}</strong> for the
          following user:
        </Typography>
        <Typography mt={2}>
          <strong>Name:</strong> {userInfo.name}
        </Typography>
        <Typography mt={1}>
          <strong>E-mail:</strong> {userInfo.email}
        </Typography>
        <Typography mt={1} mb={4}>
          <strong>Plan Name:</strong>{" "}
          {userInfo.subscriptionPlan?.name ?? "Unknown"}
        </Typography>

        <Form
          id="admin-restore-subscription"
          onSubmit={handleSubmit(onRestoreSubscription)}
          aria-disabled={isLoading}
        >
          <TextField
            name={"trialDays"}
            label={"Trial Days"}
            type="number"
            defaultValue={null}
            inputProps={{
              ...register("trialDays"),
              min: 0,
              max: TRIAL_DAYS_MAX_LENGTH,
            }}
            fieldError={errors.trialDays}
            autoFocus
          />
        </Form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="text" disabled={isLoading}>
          Go Back
        </Button>
        <SubmitButton form="admin-restore-subscription" isLoading={isLoading}>
          Resume Subscription
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
};
